import React from 'react';
import _ from 'lodash';
import {Header, Grid, Form, Divider} from 'semantic-ui-react';
import {FemaGoModal} from 'app-ui-componentlibrary';
import {createUUID, ISOToDisplayDate, safeAdd, compileContent} from 'common/utils';
import {
  ADDITIONAL_INFORMATION,
  PAYMENT,
  ANNUAL_SALARY,
  ANNUAL_BENEFITS,
  TOTAL_ELIGIBLE_COST,
  CARD_TYPES,
  FIELD_MAX_LENGTHS_SYSTEM,
} from 'common/requestDetails/constants.js';
import {GRANT_ROSTER_FIELDS} from 'app-ui-componentlibrary/dist/common/constants';
import {
  TextBoxField,
  InlineSelect,
  InlineTextBoxField,
  Checkbox,
} from 'app-ui-componentlibrary/dist/go/components/common/FormControls';
import ReadOnlyDollarSignField from 'app-ui-componentlibrary/dist/go/components/common/ReadOnlyDollarSignField';
import {TextAreaFieldFrom as TextAreaField} from 'app-ui-componentlibrary';
import {InlineDollarFieldForm as InlineDollarField} from 'app-ui-componentlibrary';
import produce from 'immer';
import {ConfigNameValue as NameValue} from 'componentLibrary';

const {
  NAME,
  START_DATE,
  END_DATE,
  COMMENTS,
  POSITION_ID,
  EMPLOYEES,
  POSITIONS,
  NOT_FUNDED,
} = GRANT_ROSTER_FIELDS;

const initialEmployee = {
  name: '',
  positionId: '',
  startDate: '',
  endDate: '',
  annualSalary: 0,
  annualBenefits: 0,
  comments: '',
  rosterId: '',
  notFunded: false,
};

const PRICE_MAX = _.get(FIELD_MAX_LENGTHS_SYSTEM, [
  CARD_TYPES.STAFFING_BUNDLED,
  ANNUAL_SALARY,
]);

class AddEmployeeModal extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      initialEmployee: initialEmployee,
    };
  }

  componentDidUpdate(prevProps) {
    if (this.props.open && !prevProps.open) {
      let toPopulate = _.cloneDeep(this.props.existingEmployee);
      // Preventing the carry over from previous modal state
      _.defaults(toPopulate, initialEmployee);
      if (!toPopulate) {
        toPopulate = initialEmployee;
      } else {
        toPopulate.startDate = ISOToDisplayDate(toPopulate.startDate, {
          showDate: true,
        });
        toPopulate.endDate = ISOToDisplayDate(toPopulate.endDate, {
          showDate: true,
        });
      }
      // This prop was added later, updating to sync
      if (!toPopulate.notFunded && !_.isEmpty(_.get(toPopulate, 'endDate', ''))) {
        toPopulate.notFunded = true;
      }

      this.setState(toPopulate);
      this.props.clearEntityData();
    }
  }

  handleChange = (field, value) => {
    let obj = {
      [field]: value,
    };
    if (field === NOT_FUNDED && value === false) {
      obj.endDate = '';
      this.handleBlur(START_DATE);
    }
    this.setState(obj);
  };

  handleBlur = (field) => {
    const fieldName = this.getFieldNameToBlur(field);
    const updatedData = {...this.state};
    const entityDataPath = 'context.employee';

    this.props.saveEntityData(entityDataPath, '', updatedData, fieldName);
  };

  getFieldNameToBlur = (fieldName) => {
    const {validationId} = this.props;
    return validationId && fieldName ? `${validationId}.${fieldName}` : false;
  };

  confirmAction = () => {
    const positions = _.get(this.props, POSITIONS, []);
    let newPositions = {};
    const employeeData = {...this.state};
    const employeePath = [this.state.positionId, EMPLOYEES];
    employeeData.startDate = ISOToDisplayDate(employeeData.startDate, {
      ISOString: true,
    });
    employeeData.endDate = ISOToDisplayDate(employeeData.endDate, {
      ISOString: true,
    });
    employeeData.rosterId = employeeData.rosterId || createUUID();

    const employeesInPosition = _.get(positions, employeePath, []);
    const existingEmployeeIndex = _.findIndex(
      employeesInPosition,
      (emp) => emp.rosterId === employeeData.rosterId
    );
    if (existingEmployeeIndex >= 0) {
      newPositions = produce(positions, (draft) => {
        _.set(draft, [...employeePath, existingEmployeeIndex], employeeData);
      });
      _.set(positions, [...employeePath, existingEmployeeIndex], employeeData);
    } else {
      newPositions = produce(positions, (draft) => {
        _.set(
          draft,
          employeePath,
          _.concat(_.get(positions, employeePath, []), employeeData)
        );
      });
    }
    this.props.confirmAction(newPositions);
  };

  renderConfirmationModal = () => {
    const {openConfirmModal, cancelAction} = this.props;
    return (
      <FemaGoModal
        parseHtml="true"
        title="Update employee information"
        size="small"
        open={openConfirmModal}
        buttonText="Confirm"
        confirmAction={() => {
          this.confirmAction();
        }}
        cancelAction={cancelAction}
      >
        <div className="modal-instructions padding-bottom">
          If you edited this employee's employment dates your payment request will be
          adjusted to only display employees who were active during the payment date
          range.
        </div>
      </FemaGoModal>
    );
  };

  render() {
    const {
      open,
      existingEmployee,
      editable,
      openConfirmModal,
      fundingStreams,
      awardedId,
    } = this.props;

    let paymentList = _.get(
      _.get(fundingStreams, awardedId),
      `${ADDITIONAL_INFORMATION}.${PAYMENT}`
    );
    const paymentStreamsModified = !_.isEmpty(paymentList);
    const {
      name,
      startDate,
      endDate,
      annualSalary,
      annualBenefits,
      comments,
      positionId,
      notFunded,
    } = this.state;
    const positions = _.get(this.props, POSITIONS, []);

    // some of these need to be configuration based
    const labels = {
      name: 'Employee name (First and Last)',
      startDate: 'Date firefighter was funded by this grant (MM/DD/YYYY)',
      endDate: 'Date when firefighter was no longer funded under this grant (MM/DD/YYYY)',
      [ANNUAL_BENEFITS]: 'Current annual benefits',
      [ANNUAL_SALARY]: 'Current annual salary',
      comments: 'Additional information',
      positionId: 'Position ID',
      [TOTAL_ELIGIBLE_COST]: 'Total',
      [NOT_FUNDED]: 'This employee is no longer funded under this grant',
    };

    const firstEmptyPositionInd = _.findIndex(positions, (pos) =>
      _.isEmpty(pos.employees)
    );
    const nonEmptyPositions =
      firstEmptyPositionInd !== -1
        ? _.slice(positions, 0, firstEmptyPositionInd)
        : positions;
    const hasAvailableNewPosition = _.size(positions) !== _.size(nonEmptyPositions);

    const positionOptions = _.map(nonEmptyPositions, (position, ind) => ({
      key: ind,
      text: ind + 1,
      value: ind,
    }));

    positionOptions.unshift({
      key: '',
      value: '',
      text: '',
    });

    if (hasAvailableNewPosition) {
      positionOptions.push({
        key: firstEmptyPositionInd,
        value: firstEmptyPositionInd,
        text: 'Add new position',
      });
    }

    const {validationDirtyMap, validationErrors, cancelAction} = this.props;

    const isValid = () => {
      const valid = _.get(validationErrors, '0.isValid');
      return valid;
    };

    const displayError = (field) => {
      const err = findError(field);
      const message = _.get(err, 'message');
      const isError = _.get(err, 'isValid') === false;

      const skipActiveEmployeeValidation =
        existingEmployee &&
        message == "You must enter an end date for this position's active employee";

      return skipActiveEmployeeValidation ? false : isError && isDirty(field);
    };

    const isDirty = (field) => _.get(validationDirtyMap, ['dirty', field], false);

    const errorMessage = (field) => {
      if (field === POSITION_ID) {
        const activeEmployeeForSelectedPosition = _.chain(this.props)
          .get(['positions', this.state.positionId, 'employees'])
          .find((e) => _.isEmpty(e.endDate))
          .get('name')
          .value();
        return compileContent(_.get(findError(field), 'message'), {
          employeeName: activeEmployeeForSelectedPosition,
        });
      } else {
        return _.get(findError(field), 'message');
      }
    };

    const findError = (field) => {
      const errs = _.get(_.get(validationErrors, 0), field);
      if (errs instanceof Array) {
        return _.find(errs, (err) => !err.isValid);
      }
      return errs;
    };

    const validInputs = isValid();
    return (
      <React.Fragment>
        <FemaGoModal
          parseHtml="true"
          title={
            existingEmployee && editable
              ? 'Edit employee'
              : existingEmployee && !editable
              ? 'View employee'
              : 'Add employee'
          }
          size="small"
          open={open}
          disableButton={!validInputs}
          buttonText={'Confirm'}
          confirmButton={editable ? 'visible' : 'hidden'}
          confirmAction={
            paymentStreamsModified
              ? () => {
                  this.props.confirmEmployee();
                }
              : () => {
                  this.confirmAction();
                }
          }
          cancelAction={cancelAction}
        >
          <Form id="employee-form">
            <Grid className={existingEmployee && !editable ? 'view-mode' : ''}>
              {editable ? (
                <Grid.Column width={existingEmployee ? 16 : 8}>
                  <InlineTextBoxField
                    value={name}
                    label={labels[NAME]}
                    name={NAME}
                    handleChange={(e) => {
                      const targetVal = e.target.value;
                      this.handleChange(NAME, targetVal);
                    }}
                    handleBlur={_.debounce(() => this.handleBlur(NAME), 250)}
                    displayError={displayError(NAME)}
                    errorMessages={errorMessage(NAME)}
                  />
                </Grid.Column>
              ) : (
                <NameValue name={labels[NAME]} data={name} notBolded={true} />
              )}
              {!existingEmployee && (
                <Grid.Column width={8}>
                  <InlineSelect
                    id={POSITION_ID}
                    options={positionOptions}
                    label={labels[POSITION_ID]}
                    name={POSITION_ID}
                    handleChange={(e) => this.handleChange(POSITION_ID, e.target.value)}
                    handleBlur={() => this.handleBlur(POSITION_ID)}
                    value={positionId}
                    displayError={displayError(POSITION_ID)}
                    errorMessages={errorMessage(POSITION_ID)}
                  />
                </Grid.Column>
              )}
            </Grid>
            <Divider />
            <Header as="h5">Employment dates</Header>
            {editable ? (
              <React.Fragment>
                <TextBoxField
                  value={startDate}
                  label={labels[START_DATE]}
                  name={START_DATE}
                  handleChange={(e) => {
                    const targetVal = e.target.value;
                    this.handleChange(START_DATE, targetVal);
                  }}
                  handleBlur={() => this.handleBlur(START_DATE)}
                  displayError={displayError(START_DATE)}
                  errorMessages={errorMessage(START_DATE)}
                />
                <Checkbox
                  id={'activePosCheckbox_' + positionId}
                  checked={notFunded}
                  label={labels[NOT_FUNDED]}
                  name={NOT_FUNDED}
                  handleBlur={() => this.handleBlur(NOT_FUNDED)}
                  handleChange={(name) => {
                    this.handleChange(NOT_FUNDED, !_.get(this.state, NOT_FUNDED, false));
                  }}
                />
                {notFunded ? (
                  <TextBoxField
                    value={endDate}
                    label={labels[END_DATE]}
                    name={END_DATE}
                    handleChange={(e) => {
                      const targetVal = e.target.value;
                      this.handleChange(END_DATE, targetVal);
                    }}
                    handleBlur={() => this.handleBlur(END_DATE)}
                    displayError={displayError(END_DATE)}
                    errorMessages={errorMessage(END_DATE)}
                  />
                ) : (
                  ''
                )}
              </React.Fragment>
            ) : (
              <React.Fragment>
                <NameValue name={labels[START_DATE]} data={startDate} notBolded={true} />
                <br />
                <NameValue name={labels[END_DATE]} data={endDate} notBolded={true} />
              </React.Fragment>
            )}
            <br />
            <Grid>
              <Grid.Column width={5}>
                <label htmlFor={ANNUAL_SALARY}>
                  {labels[ANNUAL_SALARY]}
                  {editable ? (
                    <InlineDollarField
                      value={annualSalary}
                      id={ANNUAL_SALARY}
                      name={ANNUAL_SALARY}
                      title={labels[ANNUAL_SALARY]}
                      type="text"
                      maxLengthWithoutDecimals={PRICE_MAX}
                      paddedError={false}
                      handleChange={(name, value) =>
                        this.handleChange(ANNUAL_SALARY, value)
                      }
                      alignLeft
                      handleBlur={() => this.handleBlur(ANNUAL_SALARY)}
                      displayError={displayError(ANNUAL_SALARY)}
                      errorMessages={errorMessage(ANNUAL_SALARY)}
                    />
                  ) : (
                    <ReadOnlyDollarSignField alignLeft value={annualSalary} />
                  )}
                </label>
              </Grid.Column>
              <Grid.Column width={5}>
                <label htmlFor={ANNUAL_BENEFITS}>
                  {labels[ANNUAL_BENEFITS]}
                  {editable ? (
                    <InlineDollarField
                      value={annualBenefits}
                      id={ANNUAL_BENEFITS}
                      name={ANNUAL_BENEFITS}
                      title={labels[ANNUAL_BENEFITS]}
                      type="text"
                      maxLengthWithoutDecimals={PRICE_MAX}
                      paddedError={false}
                      handleChange={(name, value) =>
                        this.handleChange(ANNUAL_BENEFITS, value)
                      }
                      alignLeft
                      handleBlur={() => this.handleBlur(ANNUAL_BENEFITS)}
                      displayError={displayError(ANNUAL_BENEFITS)}
                      errorMessages={errorMessage(ANNUAL_BENEFITS)}
                    />
                  ) : (
                    <ReadOnlyDollarSignField alignLeft value={annualBenefits} />
                  )}
                </label>
              </Grid.Column>
              <Grid.Column width={5}>
                <label htmlFor={TOTAL_ELIGIBLE_COST}>
                  {labels[TOTAL_ELIGIBLE_COST]}
                  <ReadOnlyDollarSignField
                    alignLeft
                    value={safeAdd(
                      _.get(this.state, ANNUAL_SALARY),
                      _.get(this.state, ANNUAL_BENEFITS)
                    )}
                  />
                </label>
              </Grid.Column>
            </Grid>
            <Divider />
            {editable ? (
              <TextAreaField
                className="util--full-width"
                value={comments}
                title={labels[COMMENTS]}
                label={labels[COMMENTS]}
                id={COMMENTS}
                name={COMMENTS}
                handleChange={(e, data) => this.handleChange(COMMENTS, data.value)}
                handleBlur={() => this.handleBlur(COMMENTS)}
                displayError={displayError(COMMENTS)}
                errorMessages={errorMessage(COMMENTS)}
                rows={3}
              />
            ) : (
              <NameValue name={labels[COMMENTS]} data={comments} notBolded={true} />
            )}
          </Form>
        </FemaGoModal>
        {paymentStreamsModified && openConfirmModal
          ? this.renderConfirmationModal()
          : null}
      </React.Fragment>
    );
  }
}

export default AddEmployeeModal;
